import React, { cloneElement, FC, ReactElement, useMemo } from 'react';
import { Tab, useGetOne, useTranslate } from 'react-admin';

import { PaperTabbedShowLayout } from '~/layout';
import {
  PerFeature,
  PerFees,
  PerLimits,
  PerLoyalty,
  Settings,
} from '~/resources/pricings/components';
import { hasLimitsTab, hasLoyaltyTab, l as pricingL } from '~/resources/pricings/PricingShow';
import DataTab from '~/resources/approvals/components/DataTab';
import CasesForObjectTab from '~/resources/approvals/components/CasesForObjectTab';
import { getPricingSchema, PricingSchema } from '~/api/pricings';
import { useApi, useSolution } from '~/hooks';
import { useProductById } from '~/hooks/product';
import { ApprovalCaseDetail } from '~/types/approvalCase';
import { Pricing, Solution } from '~/types';

const l = (key: string): string => `resources.approvals.${key}`;

const WithPricing: FC<{ pricing: Pricing; children: ReactElement }> = ({ pricing, children }) =>
  cloneElement(children, { record: pricing });

interface PricingApproval {
  record: Pricing;
  solution?: Solution;
  pricingSchema?: PricingSchema;
  loading: boolean;
}

const usePricingApproval = (approvalCase: ApprovalCaseDetail): PricingApproval => {
  const isCreate = approvalCase.actionType === 'CREATE';

  // An UPDATE carries only the changed fields, so solutionId and format can be
  // missing from it — and without them neither the solution nor the pricing
  // schema can be loaded.
  const { data: original, loading: originalLoading } = useGetOne(
    'pricings',
    approvalCase.objectId,
    { enabled: !isCreate && !!approvalCase.objectId }
  );

  const record = useMemo<Pricing>(() => {
    const body = (approvalCase.actionBody ?? {}) as Pricing;
    const base = isCreate ? undefined : (original as Pricing | undefined);
    return {
      ...body,
      data: body.data ?? {},
      solutionId: body.solutionId ?? base?.solutionId,
      format: body.format ?? base?.format,
    };
  }, [approvalCase.actionBody, isCreate, original]);

  const { data: solutionData } = useSolution(record.solutionId);
  const solution = solutionData?.id === record.solutionId ? solutionData : undefined;

  // Owns the product query for the whole page: this observer keeps the default
  // staleTime, so entering the pricing refetches it, while the tabs reuse the cache
  useProductById(record.productId);

  const isRecordReady = isCreate || !originalLoading;

  const [{ data: pricingSchema, loading: schemaLoading }] = useApi<PricingSchema>(() => {
    if (!isRecordReady) return Promise.resolve();
    if (record.productId) {
      return getPricingSchema({ productId: record.productId, pricingFormat: record.format });
    }
    if (record.externalSourceType) {
      return getPricingSchema({ externalSourceType: record.externalSourceType });
    }
    return Promise.resolve();
  }, [isRecordReady, record.productId, record.format, record.externalSourceType]);

  return { record, solution, pricingSchema, loading: originalLoading || schemaLoading };
};

const PricingApprovalTabs: FC<any> = (props) => {
  const translate = useTranslate();
  const t = (key: string, opts?: any): string => translate(l(key), opts);
  const tPricing = (key: string): string => translate(pricingL(key));

  const approvalCase = props.record as ApprovalCaseDetail;
  const isCreate = approvalCase.actionType === 'CREATE';

  const { record, solution, pricingSchema, loading } = usePricingApproval(approvalCase);

  return (
    <PaperTabbedShowLayout {...props}>
      <Tab label={t('tabs.data')}>
        <DataTab />
      </Tab>
      {!isCreate && (
        <Tab label={t('tabs.casesForObject')} path='cases'>
          <CasesForObjectTab />
        </Tab>
      )}
      <Tab label={tPricing('tabs.settings')} path='settings'>
        <WithPricing pricing={record}>
          <Settings loading={loading} basePath='/pricings' readOnly />
        </WithPricing>
      </Tab>
      <Tab label={tPricing('tabs.perFeatures')} path='perFeatures'>
        <WithPricing pricing={record}>
          <PerFeature record={record} pricingSchema={pricingSchema} />
        </WithPricing>
      </Tab>
      <Tab label={tPricing('tabs.fees')} path='fees'>
        <WithPricing pricing={record}>
          <PerFees record={record} loading={loading} pricingSchema={pricingSchema} />
        </WithPricing>
      </Tab>
      <Tab label={tPricing('tabs.taxes')} path='taxes'>
        <WithPricing pricing={record}>
          <PerFees record={record} loading={loading} pricingSchema={pricingSchema} tax />
        </WithPricing>
      </Tab>
      {hasLimitsTab(record) && (
        <Tab label={tPricing('tabs.limits')} path='limits'>
          <WithPricing pricing={record}>
            <PerLimits record={record} loading={loading} pricingSchema={pricingSchema} />
          </WithPricing>
        </Tab>
      )}
      {hasLoyaltyTab(solution) && (
        <Tab label={tPricing('tabs.loyalty')} path='loyalty'>
          <WithPricing pricing={record}>
            <PerLoyalty record={record} loading={loading} pricingSchema={pricingSchema} />
          </WithPricing>
        </Tab>
      )}
    </PaperTabbedShowLayout>
  );
};

export default PricingApprovalTabs;
